import type { FulfillmentOrderSummaryRoot } from '@repo/sdk'

export interface BuyerNewOrderGroup {
  /** Identity of the parcel — a buyer can appear in more than one group. */
  groupKey: string
  buyerId: string
  buyer: FulfillmentOrderSummaryRoot['buyer']
  /**
   * Delivery address every fulfillment order in this group is headed to. `null` when the address
   * is unknown (rows created before the field existed).
   */
  shippingAddressId: string | null
  fulfillmentOrders: FulfillmentOrderSummaryRoot[]
}

/**
 * A group here means **one parcel**: the fulfillment orders that go out together under a single
 * tracking number and are charged one shipping fee.
 *
 * The key is the server's `combinedShippingGroupKey`: the server alone decides what combines —
 * same buyer and delivery address, live-broadcast purchases only. Rebuilding it here as buyer +
 * address pulled marketplace orders, which always ship on their own, into a broadcast parcel's row.
 *
 * A server without the field falls back to the former buyer + address key, with unknown addresses
 * each kept in a group of their own.
 */
export function groupFulfillmentOrdersByBuyer(
  fulfillmentOrders: readonly FulfillmentOrderSummaryRoot[],
): BuyerNewOrderGroup[] {
  const groups = new Map<string, BuyerNewOrderGroup>()

  for (const fulfillmentOrder of fulfillmentOrders) {
    const buyerId = fulfillmentOrder.buyer.userId
    const shippingAddressId = fulfillmentOrder.shippingAddressId ?? null
    const serverGroupKey: string | undefined = fulfillmentOrder.combinedShippingGroupKey
    const key =
      serverGroupKey ??
      (shippingAddressId === null
        ? `${buyerId}::no-address::${fulfillmentOrder.fulfillmentOrderId}`
        : `${buyerId}::${shippingAddressId}`)

    const group = groups.get(key)
    if (group) group.fulfillmentOrders.push(fulfillmentOrder)
    else
      groups.set(key, {
        groupKey: key,
        buyerId,
        buyer: fulfillmentOrder.buyer,
        shippingAddressId,
        fulfillmentOrders: [fulfillmentOrder],
      })
  }

  return [...groups.values()]
}
